import { useEffect, useState } from 'react';
import { useAuth } from '@aimsos/auth';
import { ShellAuthProvider } from '@aimsos/auth';
import { useTranslation } from '@aimsos/i18n';
import { DataStudioApp } from '@aimsos/data-studio';
import { registerNamespace, i18n } from '../i18n/bootstrap.js';

/**
 * Mounts the Data Studio application inside the Core Shell.
 *
 * Responsibilities:
 * 1. Read the backend JWT from the unified useAuth() — backed by our own
 *    JWT, not Auth0 SDK's per-origin session, so this still mounts
 *    correctly on a {slug}.{rootDomain} subdomain landed on via a
 *    cross-subdomain handoff (see @aimsos/auth's tokenHandoff.js).
 * 2. Set window.__AIMS_SHELL__ so DataStudioApp knows it is embedded.
 * 3. Wrap DataStudioApp with ShellAuthProvider so it can read token/user via context.
 */
const DataStudioEmbed = () => {
  const { user, token, isAuthenticated, getAccessTokenSilently } = useAuth();
  const [dsShellI18nReady, setDsShellI18nReady] = useState(false);
  const [dsAppI18nReady, setDsAppI18nReady] = useState(false);
  const { t } = useTranslation('data-studio');

  /** Namespace `data-studio`: textos del shell (carga de token). */
  useEffect(() => {
    let cancelled = false;
    import('../i18n/registerDataStudio.js')
      .then(({ registerDataStudioI18n }) => {
        registerDataStudioI18n(registerNamespace);
        return i18n.loadNamespaces('data-studio');
      })
      .then(() => {
        if (!cancelled) setDsShellI18nReady(true);
      })
      .catch((err) => console.error('[DataStudioEmbed] shell i18n:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  /** Namespace `data-studio-app`: strings del paquete @aimsos/data-studio (mismo i18n que la shell). */
  useEffect(() => {
    let cancelled = false;
    import('@aimsos/data-studio/i18n/register')
      .then(({ registerDataStudioAppI18n }) => {
        registerDataStudioAppI18n(registerNamespace);
        return i18n.loadNamespaces('data-studio-app');
      })
      .then(() => {
        if (!cancelled) setDsAppI18nReady(true);
      })
      .catch((err) => console.error('[DataStudioEmbed] data-studio-app i18n:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    window.__AIMS_SHELL__ = {
      mounted: true,
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
      apiBaseUrlDS: import.meta.env.VITE_API_BASE_URL_DS,
      apiBaseUrlDataset: import.meta.env.VITE_API_BASE_URL_DATASET,
    };
    return () => {
      delete window.__AIMS_SHELL__;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || !token) {
    if (!dsShellI18nReady) return null;
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-sm text-content-muted">
        {t('embed.loadingToken')}
      </div>
    );
  }

  if (!dsAppI18nReady) return null;

  return (
    <ShellAuthProvider
      token={token}
      user={user}
      isAuthenticated={isAuthenticated}
      getAccessTokenSilently={getAccessTokenSilently}
    >
      <DataStudioApp />
    </ShellAuthProvider>
  );
};

export default DataStudioEmbed;
